import {
  CompassIcon,
  HandbagIcon,
  HeartIcon,
  HouseSimpleIcon,
  LightningIcon,
  MagnifyingGlassIcon,
  UserIcon,
} from '@phosphor-icons/react';

import { MEDIA } from '@/assets/media';
import type { PerfumeAppContent } from '@/content/types';

import styles from './PerfumeApp.module.css';

const TAB_ICONS = [HouseSimpleIcon, CompassIcon, HandbagIcon, UserIcon] as const;

/**
 * Maquette d'application d'une parfumerie : le parfum, sa pyramide olfactive,
 * le choix du flacon (le prix suit), l'ajout au panier et la barre d'onglets.
 */
export function PerfumeApp({ content }: { readonly content: PerfumeAppContent }) {
  const [small, large] = content.sizes;

  return (
    <div className={styles.app}>
      <div className={styles.topBar}>
        <span className={styles.brand}>{content.brand}</span>
        <span className={styles.icons}>
          <MagnifyingGlassIcon />
          <span className={styles.bag}>
            <HandbagIcon />
            <span className={styles.badge} />
          </span>
        </span>
      </div>

      <div className={styles.visual}>
        <img
          src={MEDIA.perfume.src}
          width={MEDIA.perfume.width}
          height={MEDIA.perfume.height}
          alt=""
          loading="lazy"
          decoding="async"
        />
      </div>

      <div className={styles.body}>
        <div className={styles.titleRow}>
          <span>
            <span className={styles.kicker}>{content.kind}</span>
            <span className={styles.name}>{content.name}</span>
          </span>
          <span className={styles.like}>
            <HeartIcon weight="fill" />
          </span>
        </div>

        {/* La pyramide olfactive : les notes de fond, les plus larges, en bas. */}
        <span className={styles.pyramid}>
          {content.notes.map((note) => (
            <span key={note.stage} className={styles.note}>
              <span className={styles.stage}>{note.stage}</span>
              <span className={styles.noteValue}>{note.value}</span>
            </span>
          ))}
        </span>

        <div className={styles.purchase}>
          <span className={styles.sizes}>
            <span className={styles.thumb} />
            <span className={styles.size}>{small.label}</span>
            <span className={styles.size}>{large.label}</span>
          </span>
          <span className={styles.prices}>
            <span className={styles.price}>{small.price}</span>
            <span className={styles.price}>{large.price}</span>
          </span>
        </div>

        <span className={styles.primary}>{content.action}</span>
        <span className={styles.express}>
          <LightningIcon weight="fill" />
          {content.express}
        </span>
        <span className={styles.reassurance}>{content.reassurance}</span>
      </div>

      <div className={styles.tabs}>
        {content.tabs.map((tab, index) => {
          const Icon = TAB_ICONS[index] ?? HouseSimpleIcon;
          return (
            <span
              key={tab}
              className={index === 1 ? `${styles.tab} ${styles.tabActive}` : styles.tab}
            >
              <Icon weight={index === 1 ? 'fill' : 'regular'} />
              {tab}
            </span>
          );
        })}
      </div>
    </div>
  );
}
